// Web Push for the Lately web client — the BROWSER half only (KSE-0185).
//
// The shared Swift core owns everything JMAP about push (RFC 8620 §7.2):
// PushSubscription/set create/verify/destroy, the stable per-install
// deviceClientId, and the persisted "which registration is verified" state,
// reachable through the shell ops registerPush / verifyPush / unregisterPush /
// listPush and reported back on every snapshot as `snapshot.push`. This module
// does the things only a page can do:
//
//   - Notification permission (from a user gesture) and PushManager.subscribe
//     with the server's VAPID applicationServerKey resolved by ./webpush.ts
//     from the JMAP session (never a baked-in key);
//   - hand endpoint + RFC 8291 keys to the core (registerPush) and, when the
//     service worker relays the server's PushVerification, hand the code back
//     (verifyPush); the SW ⇄ page contract is below;
//   - keep the browser subscription and the core's registration in step:
//     re-subscribe when the server key changes or the subscription expires,
//     re-register when the endpoint changed (pushsubscriptionchange arrives to
//     the SW; the page notices the new endpoint on its next start), unregister
//     on disable and on sign-out;
//   - refresh the shell when a StateChange push arrives while a page is open;
//   - time-box the verification handshake: Stalwart sends at most ONE
//     PushVerification per account per `jmap.pushVerifyTimeout` (60s by
//     default) and never re-sends it, so a subscription created inside that
//     window would wait forever. The controller therefore remembers the last
//     `registerPush` time per account (in memory + localStorage), never
//     creates another subscription within the window (it shows "waiting" with
//     a countdown and creates once the window has passed), and when a
//     verification is not back after 90s it unregisters the pending
//     subscription and registers a fresh one — with exponential backoff
//     (90s, 180s, 360s), at most three tries, then an error the user can
//     retry from the toggle.
//
// Service worker ⇄ page contract (the SW lives at /sw.js, scope "/", registered
// by the PWA shell in production builds and with ?sw=1 in dev):
//   SW → page  {type:"lately-push-verification", pushSubscriptionId, verificationCode}
//              (parked in IndexedDB "lately-push"/"pending" ONLY when no window
//              client exists at all, for a page opened later)
//   SW → page  {type:"lately-push-state-change", changed}
//   page → SW  {type:"lately-push-take-pending"} → SW replies to the sender
//              {type:"lately-push-pending", items:[PushVerification…]} and clears the store
//              (the page dedupes replayed items against registrations the
//              snapshot already reports as verified)
//
// Nothing here is mail logic; the mock engine gets a mock platform so the UI
// flow (toggle → enabled) can be exercised without a push service.

import type { ReactNode } from "react";
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";

import type { ShellClient } from "./shell";
import type { MailShellSnapshot, PushRegistration } from "./shell-types";
import { applicationServerKeyBytes, applicationServerKeyForSnapshot } from "./webpush";

// ---------------------------------------------------------------------------
// State

export type PushState =
  // The browser lacks Service Worker / PushManager / Notification, or the
  // server advertises no VAPID key: nothing to offer.
  | { status: "unsupported"; reason: string }
  // Notification permission was denied; only the browser's site settings can undo it.
  | { status: "blocked" }
  // Available and off.
  | { status: "disabled" }
  // Permission granted, subscribing with the push service / registering with the core.
  | { status: "subscribing" }
  // Subscribed with the push service, but the mail server would ignore a
  // subscription created before `until` (its per-account verification
  // window); the registration is made when it passes.
  | { status: "waiting"; until: number; attempt: number }
  // Registered on the server; waiting for the PushVerification to come back
  // through the service worker (`attempt` of PUSH_VERIFY_MAX_ATTEMPTS).
  | { status: "verifying"; id: string; attempt: number }
  | { status: "enabled"; id: string; expires: string | null }
  | { status: "error"; message: string };

export type PushStateStatus = PushState["status"];

// ---------------------------------------------------------------------------
// Platform adapter — the browser APIs, behind an interface so the mock engine
// (and tests) can run the same controller without a push service.

export interface PlatformPushSubscription {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  // ms since epoch, or null when the push service sets no expiry.
  expirationTime: number | null;
  // base64url of the applicationServerKey the subscription was made with
  // (null when the browser does not report it).
  applicationServerKey: string | null;
  unsubscribe(): Promise<boolean>;
}

export interface PushVerificationMessage {
  pushSubscriptionId: string;
  verificationCode: string;
}

export interface PushStateChangeMessage {
  changed: Record<string, Record<string, string>>;
}

export interface PushPlatformEvents {
  onVerification(message: PushVerificationMessage): void;
  onStateChange(message: PushStateChangeMessage): void;
}

// getSubscription() rejects with this when there is no service worker
// registration at all (first production visit racing the PWA shell's
// registerServiceWorker(), dev without ?sw=1, an evicted registration): the
// browser may well still hold a subscription, so the controller must not
// treat it as lost.
export class NoServiceWorkerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NoServiceWorkerError";
  }
}

export interface PushPlatform {
  // Service Worker + PushManager + Notification present; `unsupportedReason`
  // explains a `false`.
  readonly supported: boolean;
  readonly unsupportedReason: string | null;
  // iOS/iPadOS Safari only delivers Web Push to a Home Screen app.
  readonly isIOS: boolean;
  readonly isStandalone: boolean;
  permission(): NotificationPermission;
  requestPermission(): Promise<NotificationPermission>;
  // The current subscription for this origin, or null when the service
  // worker has none. Rejects with NoServiceWorkerError when no service worker
  // is registered yet (see there).
  getSubscription(): Promise<PlatformPushSubscription | null>;
  subscribe(applicationServerKey: string): Promise<PlatformPushSubscription>;
  // Resolves once a service worker registration is active for this page
  // (may never resolve — dev without ?sw=1).
  whenServiceWorkerReady(): Promise<void>;
  // Wire the SW → page messages; returns the unsubscribe function.
  listen(events: PushPlatformEvents): () => void;
  // Ask the SW for verifications that arrived while no page was open; null
  // when there is no service worker to ask yet (the controller asks again on
  // the next reconcile).
  takePending(): Promise<PushVerificationMessage[] | null>;
}

// Clock + timers, injectable so the deadline / window logic runs under fake
// timers in tests.
export interface PushTimers {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const realTimers: PushTimers = {
  now: () => Date.now(),
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const SW_READY_TIMEOUT_MS = 8_000;
const TAKE_PENDING_TIMEOUT_MS = 3_000;

function bufferToBase64Url(buffer: ArrayBuffer | null): string | null {
  if (!buffer) {
    return null;
  }
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function wrapSubscription(subscription: PushSubscription): PlatformPushSubscription {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: keys.p256dh ?? "", auth: keys.auth ?? "" },
    expirationTime: subscription.expirationTime ?? null,
    applicationServerKey: bufferToBase64Url(subscription.options?.applicationServerKey ?? null),
    unsubscribe: () => subscription.unsubscribe(),
  };
}

function detectIOS(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch-point count tells them apart.
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function detectStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true || nav.standalone === true
  );
}

function noServiceWorkerMessage(): string {
  return import.meta.env.DEV
    ? "No service worker is registered — open Lately with ?sw=1 in development."
    : "Lately's service worker is not registered; reload and try again.";
}

export class BrowserPushPlatform implements PushPlatform {
  readonly supported: boolean;
  readonly unsupportedReason: string | null;
  readonly isIOS = detectIOS();
  readonly isStandalone = detectStandalone();

  constructor() {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      this.supported = false;
      this.unsupportedReason = "No browser environment.";
    } else if (!("serviceWorker" in navigator)) {
      this.supported = false;
      this.unsupportedReason = "This browser has no service workers.";
    } else if (!("PushManager" in window)) {
      this.supported = false;
      this.unsupportedReason =
        this.isIOS && !this.isStandalone
          ? "On iPhone and iPad, notifications work once Lately is added to the Home Screen."
          : "This browser does not support Web Push.";
    } else if (!("Notification" in window)) {
      this.supported = false;
      this.unsupportedReason = "This browser cannot show notifications.";
    } else {
      this.supported = true;
      this.unsupportedReason = null;
    }
  }

  permission(): NotificationPermission {
    return typeof Notification === "undefined" ? "denied" : Notification.permission;
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (typeof Notification === "undefined") {
      return "denied";
    }
    // Older Safari takes a callback; the promise form is universal now, but a
    // callback-only implementation returns undefined.
    const result = await new Promise<NotificationPermission>((resolve) => {
      const maybe = Notification.requestPermission((value) => resolve(value));
      if (maybe && typeof (maybe as Promise<NotificationPermission>).then === "function") {
        void (maybe as Promise<NotificationPermission>).then(resolve);
      }
    });
    return result ?? Notification.permission;
  }

  // The registration at scope "/" if the PWA shell made one; waits (bounded)
  // for it to activate. Never registers on its own — that is the PWA shell's
  // job (and ?sw=1 in development).
  private async registration(): Promise<ServiceWorkerRegistration | null> {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration) {
      return null;
    }
    if (registration.active) {
      return registration;
    }
    const ready = await Promise.race([
      navigator.serviceWorker.ready.then((value) => value as ServiceWorkerRegistration | null),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS)),
    ]);
    return ready ?? registration;
  }

  async getSubscription(): Promise<PlatformPushSubscription | null> {
    const registration = await this.registration();
    if (!registration) {
      throw new NoServiceWorkerError(noServiceWorkerMessage());
    }
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? wrapSubscription(subscription) : null;
  }

  async subscribe(applicationServerKey: string): Promise<PlatformPushSubscription> {
    const registration = await this.registration();
    if (!registration) {
      throw new NoServiceWorkerError(noServiceWorkerMessage());
    }
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // A fresh Uint8Array (not a shared/typed view) keeps every browser's
      // BufferSource check happy.
      applicationServerKey: applicationServerKeyBytes(applicationServerKey),
    });
    return wrapSubscription(subscription);
  }

  whenServiceWorkerReady(): Promise<void> {
    return navigator.serviceWorker.ready.then(() => undefined);
  }

  listen(events: PushPlatformEvents): () => void {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown } | null;
      if (!data || typeof data !== "object") {
        return;
      }
      if (data.type === "lately-push-verification") {
        const message = data as { pushSubscriptionId?: unknown; verificationCode?: unknown };
        if (
          typeof message.pushSubscriptionId === "string" &&
          typeof message.verificationCode === "string"
        ) {
          events.onVerification({
            pushSubscriptionId: message.pushSubscriptionId,
            verificationCode: message.verificationCode,
          });
        }
      } else if (data.type === "lately-push-state-change") {
        const message = data as { changed?: unknown };
        events.onStateChange({
          changed:
            message.changed && typeof message.changed === "object"
              ? (message.changed as Record<string, Record<string, string>>)
              : {},
        });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }

  async takePending(): Promise<PushVerificationMessage[] | null> {
    const registration = await this.registration();
    const target = registration?.active ?? navigator.serviceWorker.controller;
    if (!target) {
      return null;
    }
    return new Promise<PushVerificationMessage[]>((resolve) => {
      const timer = setTimeout(() => {
        navigator.serviceWorker.removeEventListener("message", onMessage);
        resolve([]);
      }, TAKE_PENDING_TIMEOUT_MS);
      const onMessage = (event: MessageEvent) => {
        const data = event.data as { type?: unknown; items?: unknown } | null;
        if (!data || data.type !== "lately-push-pending") {
          return;
        }
        clearTimeout(timer);
        navigator.serviceWorker.removeEventListener("message", onMessage);
        const items = Array.isArray(data.items) ? data.items : [];
        resolve(
          items.flatMap((item) => {
            const candidate = item as { pushSubscriptionId?: unknown; verificationCode?: unknown };
            return typeof candidate?.pushSubscriptionId === "string" &&
              typeof candidate.verificationCode === "string"
              ? [
                  {
                    pushSubscriptionId: candidate.pushSubscriptionId,
                    verificationCode: candidate.verificationCode,
                  },
                ]
              : [];
          }),
        );
      };
      navigator.serviceWorker.addEventListener("message", onMessage);
      target.postMessage({ type: "lately-push-take-pending" });
    });
  }
}

// The mock engine's platform: no permission prompt, no push service, a
// fabricated endpoint + keys. Together with mock-worker.ts (whose registerPush
// verifies immediately) the toggle goes straight to "enabled".
export class MockPushPlatform implements PushPlatform {
  readonly supported = true;
  readonly unsupportedReason = null;
  readonly isIOS = detectIOS();
  readonly isStandalone = detectStandalone();
  private subscription: PlatformPushSubscription | null = null;
  private granted: NotificationPermission = "default";

  permission(): NotificationPermission {
    return this.granted;
  }

  async requestPermission(): Promise<NotificationPermission> {
    this.granted = "granted";
    return this.granted;
  }

  async getSubscription(): Promise<PlatformPushSubscription | null> {
    return this.subscription;
  }

  async subscribe(applicationServerKey: string): Promise<PlatformPushSubscription> {
    const random = () => Math.random().toString(36).slice(2, 12);
    const subscription: PlatformPushSubscription = {
      endpoint: `https://push.mock.lately.org/${random()}${random()}`,
      keys: { p256dh: `mock-p256dh-${random()}`, auth: `mock-auth-${random()}` },
      expirationTime: null,
      applicationServerKey,
      unsubscribe: async () => {
        if (this.subscription === subscription) {
          this.subscription = null;
        }
        return true;
      },
    };
    this.subscription = subscription;
    return subscription;
  }

  whenServiceWorkerReady(): Promise<void> {
    return Promise.resolve();
  }

  listen(): () => void {
    return () => {};
  }

  async takePending(): Promise<PushVerificationMessage[]> {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Controller

// Whether the user opted in on this browser (survives reloads so a lost or
// expired subscription is re-made silently while permission stays granted).
const WANTED_KEY = "lately.web.push.wanted";
// When the last `registerPush` (PushSubscription/set create) was made, per
// stored account: {[storageKey]: ms}. Stalwart's verification window (below)
// is per account, so this must survive a reload — a page reloaded 10s after
// disabling must still not create a doomed subscription.
const LAST_CREATE_KEY = "lately.web.push.lastCreate";
// Renew the browser subscription (and the server registration) this long
// before they expire.
const RENEW_BEFORE_MS = 24 * 60 * 60 * 1000;
// Stalwart (0.16) sends at most one PushVerification per account per
// `jmap.pushVerifyTimeout` (default 60s) and never re-sends: a subscription
// created inside the window is never verified. Never create within it (+ a
// little slack for the two clocks and the round trip).
export const PUSH_CREATE_WINDOW_MS = 60_000;
const PUSH_CREATE_WINDOW_SLACK_MS = 5_000;
// A verification not back this long after the create is treated as lost: the
// pending subscription is unregistered and a fresh one registered (the window
// above has necessarily passed by then). Doubles per attempt: 90s, 180s, 360s.
export const PUSH_VERIFY_DEADLINE_MS = 90_000;
export const PUSH_VERIFY_MAX_ATTEMPTS = 3;
// The JMAP data types the browser subscription is notified about.
export const PUSH_TYPES = ["EmailDelivery", "Email"];

type Listener = () => void;

function readWanted(): boolean {
  try {
    return localStorage.getItem(WANTED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeWanted(value: boolean) {
  try {
    if (value) {
      localStorage.setItem(WANTED_KEY, "1");
    } else {
      localStorage.removeItem(WANTED_KEY);
    }
  } catch {
    // private mode: the flag just does not persist
  }
}

function readLastCreates(): Record<string, number> {
  try {
    const raw = localStorage.getItem(LAST_CREATE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "number" && Number.isFinite(value)) {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeLastCreate(accountKey: string, at: number) {
  try {
    const all = readLastCreates();
    all[accountKey] = at;
    localStorage.setItem(LAST_CREATE_KEY, JSON.stringify(all));
  } catch {
    // private mode: the in-memory copy still covers this page's lifetime
  }
}

function describeError(cause: unknown): string {
  if (cause instanceof Error) {
    // Chromium's wording for a browser without push credentials.
    if (/push service|Registration failed/i.test(cause.message)) {
      return `The push service refused the subscription: ${cause.message}`;
    }
    return cause.message;
  }
  return String(cause);
}

function parseExpires(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

// Whether `item` belongs to the stored account (`storageKey`, JMAP
// `accountId`). JMAP account ids are short and reused per server, so two
// stored accounts on different servers can share one; the persisted
// accountStorageKey (StoredAccount.storageKey) tells their rows apart. Rows
// from a core predating that field carry none and match by accountId alone.
function belongsTo(item: PushRegistration, accountId: string, storageKey: string | null): boolean {
  if (item.accountId !== accountId) {
    return false;
  }
  if (item.accountStorageKey == null || storageKey === null) {
    return true;
  }
  return item.accountStorageKey === storageKey;
}

// The newest registration of `list` for `endpoint` (the core lists rows
// oldest first; a superseded row may linger in the snapshot for a moment).
function newestFor(list: PushRegistration[], endpoint: string): PushRegistration | null {
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const item = list[index];
    if (item && item.endpoint === endpoint) {
      return item;
    }
  }
  return null;
}

// What the controller needs from the shell client (kept structural so tests
// can drive it with a fake).
export type PushShell = Pick<ShellClient, "engine" | "snapshot" | "onSnapshot" | "command">;

export interface PushControllerOptions {
  attach?: boolean;
  timers?: PushTimers;
}

export class PushController {
  readonly platform: PushPlatform;
  private readonly shell: PushShell;
  private readonly timers: PushTimers;
  private readonly listeners = new Set<Listener>();
  private current: PushState;
  private accountId: string | null = null;
  // The active stored account's storageKey (StoredAccount.storageKey): the
  // per-account key of the verification window, and — together with
  // accountId — what identifies the account's rows in `registrations` (JMAP
  // account ids are short and reused per server; see belongsTo).
  private accountKey: string | null = null;
  private applicationServerKey: string | null = null;
  private registrations: PushRegistration[] = [];
  // storageKey → mailAccountId of every stored account in the last snapshot,
  // to notice a non-active account being deleted (see retireOrphans).
  private knownAccounts = new Map<string, string | null>();
  private activeStorageKey: string | null = null;
  private queuedVerifications: PushVerificationMessage[] = [];
  private queue: Promise<unknown> = Promise.resolve();
  private stopListening: (() => void) | null = null;
  private unsubscribeSnapshot: (() => void) | null = null;
  private pendingTaken = false;
  private attached = false;
  private lastCreates = new Map<string, number>();
  // registerPush calls made in the current enable cycle (reset by enable /
  // disable / a successful verification).
  private attempt = 0;
  private deadline: { id: string; handle: unknown } | null = null;
  private windowTimer: unknown = null;
  private waitingForServiceWorker = false;

  constructor(shell: PushShell, platform: PushPlatform, options: PushControllerOptions = {}) {
    this.shell = shell;
    this.platform = platform;
    this.timers = options.timers ?? realTimers;
    this.current = platform.supported
      ? { status: "disabled" }
      : { status: "unsupported", reason: platform.unsupportedReason ?? "Web Push is unavailable." };
    if (options.attach !== false) {
      this.attach();
    }
  }

  get state(): PushState {
    return this.current;
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Start (or resume) following the shell's snapshots and the service
  // worker's messages. Idempotent, and re-attachable after detach() — React
  // StrictMode runs an effect's cleanup and setup a second time on the same
  // memoized instance.
  attach() {
    if (this.attached) {
      return;
    }
    this.attached = true;
    if (this.platform.supported) {
      this.stopListening = this.platform.listen({
        onVerification: (message) => this.handleVerification(message),
        onStateChange: (message) => this.handleStateChange(message),
      });
    }
    this.unsubscribeSnapshot = this.shell.onSnapshot((snapshot) => this.applySnapshot(snapshot));
    this.applySnapshot(this.shell.snapshot);
  }

  // Stop following; state is kept so a later attach() continues from it
  // (timers are re-armed by the reconcile that attach() runs).
  detach() {
    if (!this.attached) {
      return;
    }
    this.attached = false;
    this.stopListening?.();
    this.stopListening = null;
    this.unsubscribeSnapshot?.();
    this.unsubscribeSnapshot = null;
    this.clearTimers();
  }

  dispose() {
    this.detach();
    this.listeners.clear();
  }

  // Called from a user gesture (the toggle): permission → subscribe → registerPush.
  enable(): Promise<void> {
    if (!this.platform.supported) {
      return Promise.resolve();
    }
    if (!this.accountId || !this.applicationServerKey) {
      this.set({
        status: "error",
        message: this.accountId
          ? "This mail server does not advertise a Web Push key."
          : "Sign in first — notifications need an open account.",
      });
      return Promise.resolve();
    }
    // Ask for permission right here, in the click's task: Safari/iOS only
    // show the prompt inside transient activation, which a reconcile in
    // flight ahead of us in the queue would outlast. Only subscribe/register
    // need serialising.
    const permission: Promise<NotificationPermission> =
      this.platform.permission() === "granted"
        ? Promise.resolve("granted")
        : this.platform.requestPermission().catch((): NotificationPermission => "denied");
    return this.enqueue(async () => {
      const granted = await permission;
      if (granted !== "granted") {
        writeWanted(false);
        this.set(granted === "denied" ? { status: "blocked" } : { status: "disabled" });
        return;
      }
      writeWanted(true);
      this.attempt = 0;
      this.clearTimers();
      await this.subscribeAndRegister();
    });
  }

  disable(): Promise<void> {
    return this.enqueue(async () => {
      writeWanted(false);
      this.attempt = 0;
      this.clearTimers();
      const accountId = this.accountId;
      const accountKey = this.accountKey;
      const subscription = await this.platform.getSubscription().catch(() => null);
      const endpoint = subscription?.endpoint ?? null;
      // Server side first (while the account is still open), then the browser.
      if (accountId) {
        for (const registration of this.mine()) {
          if (endpoint === null || registration.endpoint === endpoint) {
            await this.shell
              .command({ op: "unregisterPush", accountId, id: registration.id })
              .catch((cause) => console.warn("Lately push: unregisterPush failed", cause));
          }
        }
      }
      // One browser subscription serves every account of this install: keep
      // it while another stored account (not merely another JMAP account id
      // — see belongsTo) still has a verified registration on it.
      const sharedWithAnother =
        subscription !== null &&
        this.registrations.some(
          (item) =>
            !(accountId !== null && belongsTo(item, accountId, accountKey)) &&
            item.endpoint === subscription.endpoint &&
            item.verified,
        );
      if (subscription && !sharedWithAnother) {
        await subscription.unsubscribe().catch(() => false);
      }
      this.set(
        this.platform.permission() === "denied" ? { status: "blocked" } : { status: "disabled" },
      );
    });
  }

  // Sign-out of the last account: the core has destroyed the server side
  // (deleteAccount); drop the browser subscription and the opt-in.
  reset(): Promise<void> {
    return this.enqueue(async () => {
      writeWanted(false);
      this.attempt = 0;
      this.clearTimers();
      const subscription = await this.platform.getSubscription().catch(() => null);
      if (subscription) {
        await subscription.unsubscribe().catch(() => false);
      }
      this.accountId = null;
      this.accountKey = null;
      this.applicationServerKey = null;
      this.registrations = [];
      this.set(this.platform.supported ? { status: "disabled" } : this.current);
    });
  }

  // ---- inputs -----------------------------------------------------------

  private applySnapshot(snapshot: MailShellSnapshot) {
    const active =
      snapshot.accounts.find(
        (account) => account.storageKey === snapshot.activeAccountStorageKey,
      ) ??
      snapshot.accounts.find((account) => account.isActive) ??
      null;
    const accountId = active?.session?.mailAccountId ?? null;
    const accountKey = active?.storageKey ?? null;
    const key = applicationServerKeyForSnapshot(snapshot)?.key ?? null;
    const registrations = Array.isArray(snapshot.push) ? snapshot.push : [];
    const previousAccountId = this.accountId;
    const previousAccountKey = this.accountKey;
    const previousRegistrations = this.registrations;
    // The storage key is part of the identity: two stored accounts on
    // different servers can share a JMAP account id, and switching between
    // them must reconcile like any other account change.
    const changed =
      accountId !== previousAccountId ||
      accountKey !== previousAccountKey ||
      key !== this.applicationServerKey ||
      JSON.stringify(registrations) !== JSON.stringify(previousRegistrations);
    this.accountId = accountId;
    this.accountKey = accountKey;
    this.applicationServerKey = key;
    this.registrations = registrations;

    // Rows of a NON-active account that vanished: deleteAccount only destroys
    // the server side of the ACTIVE account (the only one with a connection);
    // a removed non-active account just loses its rows and its server
    // subscription keeps pushing to the shared browser endpoint until it
    // expires. Retire that endpoint (unsubscribe + re-subscribe) so the
    // orphan dies now.
    const previousActiveKey = this.activeStorageKey;
    const removedStorageKeys = new Set<string>();
    const removedAccountIds = new Set<string>();
    for (const [storageKey, mailAccountId] of this.knownAccounts) {
      if (
        storageKey !== previousActiveKey &&
        !snapshot.accounts.some((account) => account.storageKey === storageKey)
      ) {
        removedStorageKeys.add(storageKey);
        if (mailAccountId) {
          removedAccountIds.add(mailAccountId);
        }
      }
    }
    const orphaned = previousRegistrations.filter(
      (item) =>
        // Rows that name their stored account are matched on it; rows from
        // an older core (no accountStorageKey) fall back to the account id,
        // excluding the previously active account's own.
        (item.accountStorageKey != null
          ? removedStorageKeys.has(item.accountStorageKey)
          : item.accountId !== previousAccountId && removedAccountIds.has(item.accountId)) &&
        !registrations.some((next) => next.id === item.id && next.endpoint === item.endpoint),
    );
    this.knownAccounts = new Map(
      snapshot.accounts.map((account) => [
        account.storageKey,
        account.session?.mailAccountId ?? null,
      ]),
    );
    this.activeStorageKey = active?.storageKey ?? null;
    if (orphaned.length > 0) {
      const endpoints = new Set(orphaned.map((item) => item.endpoint));
      void this.enqueue(() => this.retireOrphans(endpoints));
    }
    if (changed && accountId) {
      void this.enqueue(() => this.reconcile());
    }
  }

  private handleVerification(message: PushVerificationMessage) {
    this.queuedVerifications.push(message);
    void this.enqueue(() => this.flushVerifications());
  }

  private handleStateChange(message: PushStateChangeMessage) {
    // The shell has one open account; refresh it when the change concerns it
    // (or when the payload names no account, to be safe).
    const accounts = Object.keys(message.changed);
    if (accounts.length === 0 || (this.accountId && accounts.includes(this.accountId))) {
      void this.shell.command({ op: "refresh" }).catch((cause) => {
        console.warn("Lately push: refresh after StateChange failed", cause);
      });
    }
  }

  // ---- reconciliation ---------------------------------------------------

  // Bring browser subscription and core registration into agreement for the
  // active account. Runs on every relevant snapshot change and on startup.
  private async reconcile() {
    if (!this.attached || !this.platform.supported) {
      return;
    }
    const accountId = this.accountId;
    if (!accountId) {
      return;
    }
    // Verifications that landed while no page was open (or before the
    // account was open) are handled first — they may flip a pending
    // registration to enabled.
    if (!this.pendingTaken) {
      const pending = await this.platform.takePending().catch(() => null);
      if (pending !== null) {
        this.pendingTaken = true;
        this.queuedVerifications.push(...pending);
      }
    }
    await this.flushVerifications();

    if (!this.applicationServerKey) {
      this.set({
        status: "unsupported",
        reason: "This mail server does not advertise a Web Push key.",
      });
      return;
    }
    if (this.platform.permission() === "denied") {
      this.set({ status: "blocked" });
      return;
    }
    const mine = this.mine();
    const wanted = readWanted();

    let subscription: PlatformPushSubscription | null;
    try {
      subscription = await this.platform.getSubscription();
    } catch (cause) {
      // No service worker registration yet (the PWA shell's
      // registerServiceWorker() is still running on a first visit; dev
      // without ?sw=1; an evicted registration) or the lookup itself failed:
      // the browser may still hold the subscription, so the server side is
      // left exactly as it is. Show what the core knows and come back once a
      // registration is active.
      if (cause instanceof NoServiceWorkerError) {
        this.deferUntilServiceWorker();
      } else {
        console.warn("Lately push: getSubscription failed", cause);
      }
      const known = mine.find((item) => item.verified) ?? mine[0] ?? null;
      if (known?.verified) {
        this.set({ status: "enabled", id: known.id, expires: known.expires ?? null });
      } else if (known) {
        this.set({ status: "verifying", id: known.id, attempt: Math.max(1, this.attempt) });
      } else {
        this.setIdle();
      }
      return;
    }

    if (!subscription) {
      if (mine.length > 0) {
        // The browser lost the subscription (expired, or the user cleared
        // site data): the server side is stale either way.
        for (const registration of mine) {
          await this.shell
            .command({ op: "unregisterPush", accountId, id: registration.id })
            .catch(() => undefined);
        }
      }
      if (wanted && this.platform.permission() === "granted") {
        // Opted in and still allowed: re-make it silently.
        await this.subscribeAndRegister();
        return;
      }
      this.setIdle();
      return;
    }

    const now = this.timers.now();
    const keyChanged =
      subscription.applicationServerKey !== null &&
      subscription.applicationServerKey !== this.applicationServerKey;
    const browserExpiring =
      subscription.expirationTime !== null && subscription.expirationTime - now < RENEW_BEFORE_MS;
    if (keyChanged || browserExpiring) {
      // The server rotated its VAPID key, or the push service is about to
      // retire this subscription: start over with a fresh one.
      for (const registration of mine) {
        await this.shell
          .command({ op: "unregisterPush", accountId, id: registration.id })
          .catch(() => undefined);
      }
      await subscription.unsubscribe().catch(() => false);
      if (wanted) {
        await this.subscribeAndRegister();
      } else {
        this.setIdle();
      }
      return;
    }

    const matching = newestFor(mine, subscription.endpoint);
    if (!matching) {
      if (!wanted) {
        // A subscription without opt-in (another account on this browser
        // enabled it, or the core's store was wiped): leave the browser
        // alone; the toggle shows off for this account.
        this.setIdle();
        return;
      }
      // Endpoint changed (pushsubscriptionchange) or the core forgot the
      // registration: register the current subscription and drop the rest.
      for (const registration of mine) {
        await this.shell
          .command({ op: "unregisterPush", accountId, id: registration.id })
          .catch(() => undefined);
      }
      await this.register(subscription);
      return;
    }

    const serverExpires = parseExpires(matching.expires);
    if (serverExpires !== null && serverExpires - now < RENEW_BEFORE_MS) {
      // Renew ahead of the server's expiry: registering the same endpoint
      // again supersedes the old row (the core destroys it) and starts a new
      // verification round. Inside the server's verification window the
      // renewal waits (there is a day of slack) instead of creating a
      // subscription that would never verify.
      const wait = this.createWindowRemaining();
      if (wait > 0) {
        this.scheduleReconcile(wait);
      } else {
        await this.register(subscription);
        return;
      }
    }
    if (matching.verified) {
      this.clearDeadline();
      this.attempt = 0;
      this.set({ status: "enabled", id: matching.id, expires: matching.expires ?? null });
    } else {
      this.set({ status: "verifying", id: matching.id, attempt: Math.max(1, this.attempt) });
      this.armDeadline(matching.id);
    }
  }

  private async subscribeAndRegister() {
    if (!this.applicationServerKey) {
      return;
    }
    this.set({ status: "subscribing" });
    let subscription: PlatformPushSubscription;
    try {
      subscription = await this.platform.subscribe(this.applicationServerKey);
    } catch (cause) {
      this.set({ status: "error", message: describeError(cause) });
      return;
    }
    await this.register(subscription);
  }

  // registerPush for `subscription` — unless the account's verification
  // window is still open, in which case the controller waits it out (state
  // "waiting" with the countdown) and reconciles again when it has passed;
  // that reconcile finds the browser subscription without a registration
  // and lands here again.
  private async register(subscription: PlatformPushSubscription) {
    const accountId = this.accountId;
    if (!accountId) {
      return;
    }
    const wait = this.createWindowRemaining();
    if (wait > 0) {
      this.set({ status: "waiting", until: this.timers.now() + wait, attempt: this.attempt + 1 });
      this.scheduleReconcile(wait);
      return;
    }
    if (this.current.status !== "subscribing") {
      this.set({ status: "subscribing" });
    }
    try {
      const result = await this.shell.command({
        op: "registerPush",
        accountId,
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        // EmailDelivery is the only signal that says "new mail arrived" (as
        // opposed to "something changed"); Email carries the state the digest
        // diffs from. Mailbox/Thread wakes would be wasted pushes.
        types: PUSH_TYPES,
        ...(subscription.expirationTime !== null
          ? { expires: new Date(subscription.expirationTime).toISOString() }
          : {}),
      });
      this.noteCreate();
      this.attempt += 1;
      // The snapshot that follows carries verified:false (or true on the
      // mock, whose registerPush verifies immediately); until it lands show
      // "verifying" and flush anything the SW already relayed.
      this.set({ status: "verifying", id: result.result.id, attempt: this.attempt });
      this.armDeadline(result.result.id);
      await this.flushVerifications();
    } catch (cause) {
      this.set({ status: "error", message: describeError(cause) });
    }
  }

  // The verification deadline for `id` passed: the server's one
  // PushVerification is not coming (created inside its window, or lost).
  // Unregister the pending subscription and register a fresh one — the
  // window has passed by now — or, after PUSH_VERIFY_MAX_ATTEMPTS, give up
  // with an error the user can retry from the toggle.
  private async expireVerification(id: string) {
    if (this.deadline?.id === id) {
      this.deadline = null;
    }
    if (!this.attached || this.current.status !== "verifying" || this.current.id !== id) {
      return;
    }
    const accountId = this.accountId;
    if (!accountId) {
      return;
    }
    const registration = this.mine().find((item) => item.id === id);
    if (registration?.verified) {
      this.attempt = 0;
      this.set({ status: "enabled", id, expires: registration.expires ?? null });
      return;
    }
    if (registration) {
      await this.shell
        .command({ op: "unregisterPush", accountId, id })
        .catch((cause) => console.warn("Lately push: unregisterPush failed", cause));
    }
    if (this.attempt >= PUSH_VERIFY_MAX_ATTEMPTS) {
      // Opt out so the reconcile the unregister triggers does not start the
      // cycle again; the toggle (or "Try again") opts back in.
      writeWanted(false);
      this.attempt = 0;
      this.set({
        status: "error",
        message: `The mail server's verification push never arrived (${PUSH_VERIFY_MAX_ATTEMPTS} tries). Try again in a minute.`,
      });
      return;
    }
    let subscription: PlatformPushSubscription | null = null;
    try {
      subscription = await this.platform.getSubscription();
    } catch {
      subscription = null;
    }
    if (subscription) {
      await this.register(subscription);
    } else {
      await this.reconcile();
    }
  }

  // A non-active account was removed while it still had registrations on
  // `endpoints`: retire the browser subscription that serves them (its
  // server subscription would otherwise keep pushing until it expires) and,
  // when the active account uses push, re-make it on a fresh endpoint.
  private async retireOrphans(endpoints: Set<string>) {
    if (!this.attached || !this.platform.supported) {
      return;
    }
    let subscription: PlatformPushSubscription | null;
    try {
      subscription = await this.platform.getSubscription();
    } catch {
      return;
    }
    if (!subscription || !endpoints.has(subscription.endpoint)) {
      return;
    }
    const accountId = this.accountId;
    const mineOnEndpoint = this.mine().filter((item) => item.endpoint === subscription.endpoint);
    for (const registration of mineOnEndpoint) {
      await this.shell
        .command({ op: "unregisterPush", accountId: registration.accountId, id: registration.id })
        .catch(() => undefined);
    }
    await subscription.unsubscribe().catch(() => false);
    this.clearTimers();
    const wanted = readWanted() || mineOnEndpoint.length > 0;
    if (
      accountId &&
      wanted &&
      this.applicationServerKey &&
      this.platform.permission() === "granted"
    ) {
      writeWanted(true);
      this.attempt = 0;
      await this.subscribeAndRegister();
      return;
    }
    this.setIdle();
  }

  private async flushVerifications() {
    const accountId = this.accountId;
    if (!accountId || this.queuedVerifications.length === 0) {
      return;
    }
    const batch = this.queuedVerifications.splice(0);
    for (const message of batch) {
      // Looked up among the active account's rows only: PushSubscription ids
      // are per server, so another stored account may hold the same id.
      const known = this.mine().find((item) => item.id === message.pushSubscriptionId);
      if (known?.verified) {
        // Replayed from the service worker's parked store (or delivered
        // twice): the core already has this one verified — no second
        // PushSubscription/set.
        if (this.current.status === "verifying" && this.current.id === known.id) {
          this.clearDeadline();
          this.attempt = 0;
          this.set({ status: "enabled", id: known.id, expires: known.expires ?? null });
        }
        continue;
      }
      try {
        await this.shell.command({
          op: "verifyPush",
          accountId,
          id: message.pushSubscriptionId,
          verificationCode: message.verificationCode,
        });
        if (this.current.status === "verifying" && this.current.id === message.pushSubscriptionId) {
          const registration = this.mine().find((item) => item.id === message.pushSubscriptionId);
          this.clearDeadline();
          this.attempt = 0;
          this.set({
            status: "enabled",
            id: message.pushSubscriptionId,
            expires: registration?.expires ?? null,
          });
        }
      } catch (cause) {
        // A code for a subscription this session does not own (another
        // account's, or already superseded) is not an error for the toggle.
        console.warn(`Lately push: verifyPush(${message.pushSubscriptionId}) failed`, cause);
        if (this.current.status === "verifying" && this.current.id === message.pushSubscriptionId) {
          this.set({ status: "error", message: describeError(cause) });
        }
      }
    }
  }

  // ---- the server's verification window + the deadline ------------------

  private lastCreateAt(): number | null {
    const key = this.accountKey;
    if (!key) {
      return null;
    }
    const persisted = readLastCreates()[key];
    const memory = this.lastCreates.get(key);
    const candidates = [persisted, memory].filter(
      (value): value is number => typeof value === "number",
    );
    return candidates.length > 0 ? Math.max(...candidates) : null;
  }

  private noteCreate() {
    const key = this.accountKey;
    if (!key) {
      return;
    }
    const now = this.timers.now();
    this.lastCreates.set(key, now);
    writeLastCreate(key, now);
  }

  // ms until a new subscription for the active account can be verified by
  // the server (0 when it can be created now).
  private createWindowRemaining(): number {
    const last = this.lastCreateAt();
    if (last === null) {
      return 0;
    }
    const until = last + PUSH_CREATE_WINDOW_MS + PUSH_CREATE_WINDOW_SLACK_MS;
    return Math.max(0, until - this.timers.now());
  }

  private scheduleReconcile(ms: number) {
    if (this.windowTimer !== null) {
      this.timers.clearTimeout(this.windowTimer);
    }
    this.windowTimer = this.timers.setTimeout(() => {
      this.windowTimer = null;
      void this.enqueue(() => this.reconcile());
    }, ms);
  }

  // Arm the verification deadline for registration `id`, measured from the
  // account's last create (persisted, so a reload keeps the same deadline)
  // and doubling per attempt. Already past → expire now.
  private armDeadline(id: string) {
    if (this.deadline?.id === id) {
      return;
    }
    this.clearDeadline();
    const attempt = Math.max(1, this.attempt);
    const base = this.lastCreateAt() ?? this.timers.now();
    const due = base + PUSH_VERIFY_DEADLINE_MS * 2 ** (attempt - 1) - this.timers.now();
    const fire = () => {
      void this.enqueue(() => this.expireVerification(id));
    };
    this.deadline = { id, handle: this.timers.setTimeout(fire, Math.max(0, due)) };
  }

  private clearDeadline() {
    if (this.deadline) {
      this.timers.clearTimeout(this.deadline.handle);
      this.deadline = null;
    }
  }

  private clearTimers() {
    this.clearDeadline();
    if (this.windowTimer !== null) {
      this.timers.clearTimeout(this.windowTimer);
      this.windowTimer = null;
    }
  }

  private deferUntilServiceWorker() {
    if (this.waitingForServiceWorker) {
      return;
    }
    this.waitingForServiceWorker = true;
    void this.platform.whenServiceWorkerReady().then(
      () => {
        this.waitingForServiceWorker = false;
        if (this.attached) {
          void this.enqueue(() => this.reconcile());
        }
      },
      () => {
        this.waitingForServiceWorker = false;
      },
    );
  }

  // ---- plumbing ---------------------------------------------------------

  // The active stored account's registrations (by storage key and account
  // id, see belongsTo); empty without an open account.
  private mine(): PushRegistration[] {
    const accountId = this.accountId;
    if (!accountId) {
      return [];
    }
    return this.registrations.filter((item) => belongsTo(item, accountId, this.accountKey));
  }

  // Off, unless permission is denied (blocked) or an error is showing: an
  // error stays until the user acts on the toggle, so a snapshot arriving
  // right after (e.g. from the unregister that preceded it) does not wipe it.
  private setIdle() {
    if (this.platform.permission() === "denied") {
      this.set({ status: "blocked" });
    } else if (this.current.status !== "error") {
      this.set({ status: "disabled" });
    }
  }

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const next = this.queue.then(task, task);
    this.queue = next.catch(() => {});
    return next;
  }

  private set(state: PushState) {
    const previous = this.current;
    if (JSON.stringify(previous) === JSON.stringify(state)) {
      return;
    }
    this.current = state;
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (cause) {
        console.error("Lately push: listener failed", cause);
      }
    }
  }
}

export function createPushController(
  shell: PushShell,
  options: PushControllerOptions = {},
): PushController {
  const platform =
    shell.engine === "mock-shell" ? new MockPushPlatform() : new BrowserPushPlatform();
  return new PushController(shell, platform, options);
}

// ---------------------------------------------------------------------------
// React bindings

const PushContext = createContext<PushController | null>(null);

export function PushProvider({
  shell,
  signedOut,
  children,
}: {
  shell: ShellClient;
  // True when no account remains (sign-out of the last one): the browser
  // subscription is dropped along with the core's state.
  signedOut: boolean;
  children?: ReactNode;
}) {
  // Created detached and attached by the effect: StrictMode runs the effect
  // twice on the same memoized instance, so cleanup must be resumable.
  const controller = useMemo(() => createPushController(shell, { attach: false }), [shell]);
  useEffect(() => {
    controller.attach();
    return () => controller.detach();
  }, [controller]);
  useEffect(() => {
    if (signedOut) {
      void controller.reset();
    }
  }, [controller, signedOut]);
  return createElement(PushContext.Provider, { value: controller }, children);
}

export function usePush(): { controller: PushController; state: PushState } {
  const controller = useContext(PushContext);
  if (!controller) {
    throw new Error("usePush requires a PushProvider ancestor");
  }
  const state = useSyncExternalStore(
    (onChange) => controller.onChange(onChange),
    () => controller.state,
    () => controller.state,
  );
  return { controller, state };
}
